import { AnimatePresence, motion } from 'framer-motion'
import AttachmentLinks from '@/components/common/AttachmentLinks'
import { useEffect, useMemo, useState } from 'react'
import { eventsApi, type ClassEventResponse } from '@/api/endpoints/events'
import { classesApi, type ClassResponse } from '@/api/endpoints/classes'
import { studentsApi } from '@/api/endpoints/students'
import { parishesApi } from '@/api/endpoints/parishes'
import { useAuth } from '@/stores/auth'
import { useUI } from '@/stores/ui'
import { printClassStudentList } from '@/utils/print'
import { getHolidaysForYear, isInHolidayWeek } from '@/utils/holidays'
import { toLocalDateString, toLocalDateTimeString, parseLocalDate, isDateOnlyEvent, formatEventWhen } from '@/utils/date'
import { generateOccurrences } from '@/utils/recurrence'
import Spinner from '@/components/common/Spinner'
import EventEditModal from './EventEditModal'

interface Props {
  open: boolean
  onClose: () => void
  cls: { id: string; name: string }
}

export default function ClassCalendarModal({ open, onClose, cls }: Props) {
  const { user } = useAuth()
  const addToast = useUI((state) => state.addToast)
  const [loading, setLoading] = useState(false)
  const [events, setEvents] = useState<ClassEventResponse[]>([])
  const [classDetails, setClassDetails] = useState<ClassResponse | null>(null)
  const [parishName, setParishName] = useState('')
  const [birthdayReminders, setBirthdayReminders] = useState<Array<{ student_id: string; student_name: string; birthday_date: string; allergen_alerts: string[] }>>([])
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<ClassEventResponse | null>(null)
  const [showEventModal, setShowEventModal] = useState(false)
  const [topic, setTopic] = useState('')
  const [notes, setNotes] = useState('')
  const [date, setDate] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [fcLoaded, setFcLoaded] = useState(false)
  const [FullCalendarComp, setFullCalendarComp] = useState<any>(null)
  const [fcPlugins, setFcPlugins] = useState<any[]>([])
  const [calendarRef, setCalendarRef] = useState<any>(null)
  const [repeatEnabled, setRepeatEnabled] = useState(false)
  const [repeatFreq, setRepeatFreq] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>('WEEKLY')
  const [repeatInterval, setRepeatInterval] = useState<number>(1)
  const [repeatUntil, setRepeatUntil] = useState('')
  const [repeatCount, setRepeatCount] = useState<number | ''>('')
  const [repeatEndMode, setRepeatEndMode] = useState<'until' | 'count'>('until')
  const [skipHolidays, setSkipHolidays] = useState(true)
  const [repeatWeekdays, setRepeatWeekdays] = useState<Set<number>>(new Set()) // 0=Sun, 1=Mon, ..., 6=Sat
  const [rruleLoaded, setRruleLoaded] = useState(false)
  const [RRuleObj, setRRuleObj] = useState<any>(null)
  const [monthlyMode, setMonthlyMode] = useState<'DAY_OF_MONTH' | 'NTH_WEEKDAY'>('DAY_OF_MONTH')
  const [monthlyOrdinal, setMonthlyOrdinal] = useState<number>(1) // 1..4 = first..fourth, -1 = last
  const [holidays, setHolidays] = useState<Array<{ date: Date; name: string }>>([])
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set())
  const [clipboardEvents, setClipboardEvents] = useState<Array<{ event: ClassEventResponse; originalDate: string }>>([])
  const [clipboardMode, setClipboardMode] = useState<'copy' | 'cut' | null>(null)
  const [isSelecting, setIsSelecting] = useState(false)
  const [draggedEventId, setDraggedEventId] = useState<string | null>(null)
  const [listPasteOpen, setListPasteOpen] = useState(false)
  const [listPasteDate, setListPasteDate] = useState('')
  const [dropOverId, setDropOverId] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    const run = async () => {
      if (!open || !cls.id) return
      setLoading(true)
      try {
        console.log('Loading calendar data for class:', cls.id)
        const [eventsData, classData, parishesData] = await Promise.all([
          eventsApi.list(cls.id),
          classesApi.list().then(classes => classes.find(c => c.id === cls.id) || null),
          user?.parish_id ? parishesApi.list().then(parishes => {
            const active = parishes.find(p => p.id === user.parish_id)
            return active?.name || ''
          }) : Promise.resolve(''),
        ])
        
        console.log('Loaded events:', eventsData?.length || 0, eventsData)
        
        // Load birthday reminders separately - don't fail if this errors
        let birthdayData: any[] = []
        try {
          birthdayData = await studentsApi.getBirthdayReminders(cls.id, 366)
        } catch (error) {
          console.warn('Failed to load birthday reminders on initial load:', error)
        }
        
        if (mounted) {
          setEvents(Array.isArray(eventsData) ? eventsData : [])
          setClassDetails(classData)
          setParishName(parishesData)
          setBirthdayReminders(Array.isArray(birthdayData) ? birthdayData : [])
          
          // Load holidays for current year and adjacent years
          const year = new Date().getFullYear()
          const allHolidays = [
            ...getHolidaysForYear(year - 1),
            ...getHolidaysForYear(year),
            ...getHolidaysForYear(year + 1),
          ].map(h => ({ date: h.date, name: h.name }))
          console.log('Loaded holidays:', allHolidays.length, allHolidays)
          setHolidays(allHolidays)
        }
      } catch (error) {
        console.error('Failed to load calendar data:', error)
        if (mounted) {
          setEvents([])
          setClassDetails(null)
          setParishName('')
          setBirthdayReminders([])
          setHolidays([])
        }
      } finally {
        if (mounted) setLoading(false)
      }
    }
    run()
    return () => { mounted = false }
  }, [open, cls.id, user?.parish_id])

  // Dynamically load FullCalendar (avoids breaking build if not installed yet)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [{ default: FullCalendar }, dayGrid, interaction] = await Promise.all([
          import(/* @vite-ignore */'@fullcalendar/react'),
          import(/* @vite-ignore */'@fullcalendar/daygrid'),
          import(/* @vite-ignore */'@fullcalendar/interaction'),
        ])
        if (!cancelled) {
          setFullCalendarComp(() => FullCalendar)
          setFcPlugins([dayGrid.default, interaction.default])
          setFcLoaded(true)
        }
      } catch {
        if (!cancelled) setFcLoaded(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Dynamically (safely) load rrule for recurrence support
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const mod = await import(/* @vite-ignore */'rrule')
        if (!cancelled) {
          setRRuleObj(mod)
          setRruleLoaded(true)
        }
      } catch {
        if (!cancelled) setRruleLoaded(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const sorted = useMemo(() => {
    return [...events].sort((a, b) => {
      const dateA = a.starts_at || a.event_date || ''
      const dateB = b.starts_at || b.event_date || ''
      return dateA.localeCompare(dateB)
    })
  }, [events])

  // Prepare calendar events including holidays and birthdays
  const calendarEvents = useMemo(() => {
    const evts: Array<{
      id: string
      title: string
      start: string
      backgroundColor?: string
      borderColor?: string
      extendedProps?: any
    }> = []

    // Add class events
    events.forEach(ev => {
      if (ev.starts_at || ev.event_date) {
        // Ensure date is in correct format for FullCalendar
        let eventStart: string
        if (ev.starts_at) {
          eventStart = ev.starts_at
        } else if (ev.event_date) {
          // If event_date is YYYY-MM-DD, convert to ISO datetime for FullCalendar
          const dateStr = ev.event_date
          if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // YYYY-MM-DD format - use as-is (FullCalendar accepts this)
            eventStart = dateStr
          } else {
            // Try to parse as ISO
            eventStart = new Date(dateStr).toISOString()
          }
        } else {
          return // Skip if no date
        }
        
        const isMidnight = isDateOnlyEvent(ev.event_date)
        evts.push({
          id: ev.id,
          title: ev.topic,
          start: isMidnight ? (ev.event_date || '').slice(0, 10) : eventStart,
          ...(isMidnight ? { allDay: true } : {}),
          backgroundColor: '#3b82f6',
          borderColor: '#2563eb',
          extendedProps: { type: 'event', event: ev },
        })
      }
    })

    // Add holidays - ensure date is in YYYY-MM-DD format for FullCalendar
    holidays.forEach(holiday => {
      try {
        // Handle both Date objects and date strings
        let holidayDate: Date
        if (holiday.date instanceof Date) {
          holidayDate = new Date(holiday.date)
        } else if (typeof holiday.date === 'string') {
          holidayDate = new Date(holiday.date)
        } else {
          // If it's already a date-like object, use it directly
          holidayDate = new Date(holiday.date)
        }
        
        // Validate date
        if (isNaN(holidayDate.getTime())) {
          console.warn('Invalid holiday date:', holiday)
          return
        }
        
        // Set to midnight to avoid timezone issues
        holidayDate.setHours(0, 0, 0, 0)
        const dateStr = toLocalDateString(holidayDate) // YYYY-MM-DD (local, no UTC shift)
        
        evts.push({
          id: `holiday-${dateStr}-${holiday.name.replace(/\s+/g, '-')}`,
          title: holiday.name,
          start: dateStr,
          allDay: true,
          backgroundColor: '#f59e0b',
          borderColor: '#d97706',
          extendedProps: { type: 'holiday' },
        } as any) // Type assertion needed for FullCalendar's allDay property
      } catch (error) {
        console.error('Error processing holiday:', holiday, error)
      }
    })

    // Add birthdays (spec: every assigned child's DOB is noted on the class calendar, with allergens).
    // Projected over the same years as holidays; dates parsed as LOCAL dates (no UTC day shift).
    const thisYear = new Date().getFullYear()
    birthdayReminders.forEach(bday => {
      const dob = parseLocalDate((bday as any).date_of_birth || bday.birthday_date)
      const allergenText = bday.allergen_alerts && bday.allergen_alerts.length > 0
        ? ` (⚠️ Allergen: ${bday.allergen_alerts.join(', ')})`
        : ''
      for (let y = thisYear - 1; y <= thisYear + 1; y++) {
        let d = new Date(y, dob.getMonth(), dob.getDate())
        if (d.getMonth() !== dob.getMonth()) d = new Date(y, 1, 28) // Feb 29 in a non-leap year
        evts.push({
          id: `birthday-${bday.student_id}-${y}`,
          title: `🎂 ${bday.student_name}${allergenText}`,
          start: toLocalDateString(d),
          allDay: true,
          backgroundColor: '#ec4899',
          borderColor: '#db2777',
          extendedProps: { type: 'birthday', student: bday },
        } as any)
      }
    })

    return evts
  }, [events, holidays, birthdayReminders])

  // Force calendar to refresh when events change
  useEffect(() => {
    if (viewMode === 'calendar' && calendarRef && calendarRef.getApi) {
      try {
        const api = calendarRef.getApi()
        // Use a small delay to ensure state has updated
        setTimeout(() => {
          api.refetchEvents()
        }, 100)
      } catch (e) {
        console.error('Failed to refetch events on change:', e)
      }
    }
  }, [events, viewMode, calendarRef])

  const handleEventClick = (info: any) => {
    if (info.event.extendedProps?.type === 'event' && info.event.extendedProps?.event) {
      setSelectedEvent(info.event.extendedProps.event)
      setShowEventModal(true)
    }
  }

  const handleRefresh = async () => {
    try {
      const eventsData = await eventsApi.list(cls.id)
      setEvents(Array.isArray(eventsData) ? eventsData : [])
      
      // Load birthday reminders separately - don't fail if this errors
      try {
        const birthdayData = await studentsApi.getBirthdayReminders(cls.id, 366)
        setBirthdayReminders(Array.isArray(birthdayData) ? birthdayData : [])
      } catch (birthdayError) {
        console.warn('Failed to load birthday reminders:', birthdayError)
        setBirthdayReminders([])
      }
    } catch (error) {
      console.error('Failed to refresh events:', error)
    }
  }

  const handleCopy = () => {
    const selectedEvents = events.filter(e => selectedEventIds.has(e.id))
    setClipboardEvents(selectedEvents.map(e => ({
      event: e,
      originalDate: e.event_date || '',
    })))
    setClipboardMode('copy')
    addToast({ message: `Copied ${selectedEvents.length} event(s)`, type: 'success' })
  }

  const handleCut = () => {
    const selectedEvents = events.filter(e => selectedEventIds.has(e.id))
    setClipboardEvents(selectedEvents.map(e => ({
      event: e,
      originalDate: e.event_date || '',
    })))
    setClipboardMode('cut')
    addToast({ message: `Cut ${selectedEvents.length} event(s). Click where to paste.`, type: 'info' })
  }

  const handlePaste = async (targetDate?: string) => {
    if (clipboardEvents.length === 0) {
      addToast({ message: 'No events in clipboard', type: 'warning' })
      return
    }

    // If targetDate provided (from dateClick), use it; otherwise ask user to click
    if (!targetDate) {
      if (viewMode === 'list') {
        setListPasteOpen(true)
        return
      }
      addToast({ message: 'Click on a date to paste events there', type: 'info' })
      setIsSelecting(true)
      return
    }
    setListPasteOpen(false)

    const target = parseLocalDate(targetDate)
    const results: Array<{ success: boolean; error?: string }> = []
    // The earliest copied event lands on the chosen day; the others keep their spacing.
    const anchor = Math.min(...clipboardEvents.map(c => parseLocalDate(c.originalDate).getTime()))
    const dateDiff = Math.round((target.getTime() - anchor) / (1000 * 60 * 60 * 24))

    for (const clipboardItem of clipboardEvents) {
      // Apply the day delta to the full original datetime so time-of-day is preserved.
      const newDate = new Date(clipboardItem.originalDate)
      newDate.setDate(newDate.getDate() + dateDiff)
      const newDateStr = toLocalDateTimeString(newDate)

      try {
        if (clipboardMode === 'cut') {
          // Update existing event
          await eventsApi.update(clipboardItem.event.id, { event_date: newDateStr })
        } else {
          // Create new event
          await eventsApi.create({
            class_id: cls.id,
            topic: clipboardItem.event.topic,
            notes: clipboardItem.event.notes || null,
            event_date: newDateStr,
            is_recurring: clipboardItem.event.is_recurring || false,
            recurrence_rule: clipboardItem.event.recurrence_rule || null,
          })
        }
        results.push({ success: true })
      } catch (error: any) {
        results.push({ success: false, error: error.message })
      }
    }

    const successCount = results.filter(r => r.success).length
    if (successCount === clipboardEvents.length) {
      addToast({ 
        message: clipboardMode === 'cut' 
          ? `Moved ${successCount} event(s) successfully` 
          : `Pasted ${successCount} event(s) successfully`,
        type: 'success' 
      })
      setClipboardMode(null)
      setClipboardEvents([])
      if (clipboardMode === 'cut') {
        setSelectedEventIds(new Set())
      }
      await handleRefresh()
    } else {
      addToast({ 
        message: `Pasted ${successCount}/${clipboardEvents.length} events. Some failed.`, 
        type: 'warning' 
      })
    }
    setIsSelecting(false)
  }

  const moveEventsByDays = async (ids: string[], dayDelta: number) => {
    if (!dayDelta || ids.length === 0) return
    const previous = [...events]
    const targets = events.filter(e => ids.includes(e.id) && e.event_date)
    const shifted = (e: ClassEventResponse) => {
      const d = new Date(e.event_date as string)
      d.setDate(d.getDate() + dayDelta)
      return toLocalDateTimeString(d)
    }
    setEvents(prev => prev.map(e => (ids.includes(e.id) && e.event_date ? { ...e, event_date: shifted(e) } : e)))
    try {
      await Promise.all(targets.map(e => eventsApi.update(e.id, { event_date: shifted(e) })))
      addToast({ message: targets.length > 1 ? `Moved ${targets.length} events` : 'Event moved', type: 'success' })
      setSelectedEventIds(new Set())
      await handleRefresh()
    } catch (error: any) {
      setEvents(previous)
      addToast({ message: error?.response?.data?.detail || 'Failed to move event(s)', type: 'error' })
      await handleRefresh()
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedEventIds.size === 0) return
    
    if (!confirm(`Delete ${selectedEventIds.size} selected event(s)?`)) return

    const ids = Array.from(selectedEventIds)
    const promises = ids.map(id => eventsApi.remove(id).catch(err => ({ error: err })))
    const results = await Promise.all(promises)
    
    const successCount = results.filter(r => !('error' in r)).length
    if (successCount === ids.length) {
      addToast({ message: `Deleted ${successCount} event(s)`, type: 'success' })
      setSelectedEventIds(new Set())
      await handleRefresh()
    } else {
      addToast({ message: `Deleted ${successCount}/${ids.length} events. Some failed.`, type: 'warning' })
      await handleRefresh()
    }
  }

  const toggleEventSelection = (eventId: string, ctrlKey: boolean) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev)
      if (ctrlKey) {
        if (next.has(eventId)) {
          next.delete(eventId)
        } else {
          next.add(eventId)
        }
      } else {
        return new Set([eventId])
      }
      return next
    })
  }

  // Keyboard shortcuts for copy/paste/cut
  useEffect(() => {
    if (!open || showEventModal) return
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not typing in an input
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey

      // Ctrl/Cmd+C: Copy selected events
      if (ctrlKey && e.key === 'c' && selectedEventIds.size > 0) {
        e.preventDefault()
        handleCopy()
      }
      // Ctrl/Cmd+X: Cut selected events
      if (ctrlKey && e.key === 'x' && selectedEventIds.size > 0) {
        e.preventDefault()
        handleCut()
      }
      // Ctrl/Cmd+V: Paste events
      if (ctrlKey && e.key === 'v' && clipboardEvents.length > 0) {
        e.preventDefault()
        handlePaste()
      }
      // Ctrl/Cmd+A: Select all events
      if (ctrlKey && e.key === 'a') {
        e.preventDefault()
        const allEventIds = events.filter(e => e.event_date).map(e => e.id)
        setSelectedEventIds(new Set(allEventIds))
      }
      // Escape: Deselect all
      if (e.key === 'Escape') {
        setSelectedEventIds(new Set())
        setIsSelecting(false)
        setListPasteOpen(false)
      }
      // Delete: Delete selected events
      if (e.key === 'Delete' && selectedEventIds.size > 0) {
        e.preventDefault()
        handleDeleteSelected()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, showEventModal, viewMode, selectedEventIds, clipboardEvents, events, handleCopy, handleCut, handlePaste, handleDeleteSelected])

  // Weekly recurrence requires the start date's weekday to be among the selected
  // repeat days. We validate (rather than auto-fix) so the user stays in control.
  const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const startWeekday = date ? new Date(date).getDay() : null
  const weeklyStartMismatch =
    repeatEnabled && repeatFreq === 'WEEKLY' && startWeekday !== null &&
    (repeatWeekdays.size === 0 || !repeatWeekdays.has(startWeekday))

  // Live preview of the dates a recurring event will create (shown under the form).
  const fmtPreview = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const recurrencePreview = useMemo(() => {
    if (!repeatEnabled || !date || weeklyStartMismatch) return []
    try {
      return generateOccurrences(new Date(date), {
        repeatEnabled, repeatFreq, repeatInterval, repeatEndMode, repeatUntil,
        repeatCount, repeatWeekdays, monthlyMode, monthlyOrdinal, skipHolidays,
        holidays, rrule: rruleLoaded ? RRuleObj : null,
      })
    } catch {
      return []
    }
  }, [repeatEnabled, date, weeklyStartMismatch, repeatFreq, repeatInterval, repeatEndMode, repeatUntil, repeatCount, repeatWeekdays, monthlyMode, monthlyOrdinal, skipHolidays, holidays, rruleLoaded, RRuleObj])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl p-4 sm:p-6 hover-raise mx-2 sm:mx-4 max-h-[95vh] flex flex-col pointer-events-auto"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{cls.name}</h2>
                <p className="text-sm text-gray-500 mt-1">Calendar & Events</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {(
                  <div className="flex items-center gap-1 border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-50 p-1">
                    <button
                      className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleCopy}
                      disabled={selectedEventIds.size === 0}
                      title="Copy selected events (Ctrl+C)"
                      aria-label="Copy selected events"
                    >
                      📋 Copy
                    </button>
                    <button
                      className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleCut}
                      disabled={selectedEventIds.size === 0}
                      title="Cut selected events (Ctrl+X)"
                      aria-label="Cut selected events"
                    >
                      ✂️ Cut
                    </button>
                    <button
                      className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handlePaste()}
                      disabled={clipboardEvents.length === 0}
                      title="Paste events (Ctrl+V, then click date)"
                      aria-label="Paste events"
                    >
                      📌 Paste
                    </button>
                    <button
                      className="btn-secondary text-xs px-3 py-1.5 text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleDeleteSelected}
                      disabled={selectedEventIds.size === 0}
                      title="Delete selected events (Delete key)"
                      aria-label="Delete selected events"
                    >
                      🗑️ Delete
                    </button>
                    {selectedEventIds.size > 0 && (
                      <span className="px-2 py-1 text-xs text-gray-600 font-medium">
                        {selectedEventIds.size} selected
                      </span>
                    )}
                    {listPasteOpen && viewMode === 'list' && clipboardEvents.length > 0 && (
                      <form
                        className="flex items-center gap-1 px-1"
                        onSubmit={(e) => { e.preventDefault(); if (listPasteDate) handlePaste(listPasteDate) }}
                      >
                        <label className="text-xs text-gray-600" htmlFor="list-paste-date">Paste to</label>
                        <input id="list-paste-date" type="date" className="input-field text-xs py-1" value={listPasteDate}
                          onChange={(e) => setListPasteDate(e.target.value)} aria-label="Paste to date" required />
                        <button type="submit" className="btn-primary text-xs px-2 py-1" aria-label="Paste here">OK</button>
                      </form>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-1 border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                  <button
                    className={`px-4 py-2 text-sm font-medium transition-all duration-200 focus-ring ${
                      viewMode === 'calendar'
                        ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={() => setViewMode('calendar')}
                    aria-label="Switch to calendar view"
                  >
                    📅 Calendar
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-medium transition-all duration-200 focus-ring ${
                      viewMode === 'list'
                        ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={() => {
                      setViewMode('list')
                      setShowCreateForm(false) // Don't show form by default
                    }}
                    aria-label="Switch to list view"
                  >
                    📋 List
                  </button>
                </div>
                <button
                  className="btn-secondary text-sm focus-ring"
                  onClick={async () => {
                    if (viewMode === 'calendar' && calendarRef && calendarRef.getApi) {
                      // Print calendar view
                      try {
                        const calendarApi = calendarRef.getApi()
                        const view = calendarApi.view
                        const title = `${classDetails?.name || cls.name} - ${view.title}`
                        
                        // Create a new window for printing
                        const printWindow = window.open('', '_blank')
                        if (!printWindow) {
                          addToast({ message: 'Please allow popups to print calendar', type: 'error' })
                          return
                        }
                        
                        const esc = (v: string) => String(v).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c])
                        // Get current month's events (filter out birthdays for print)
                        const currentEvents = calendarEvents.filter(evt => evt.extendedProps?.type !== 'birthday')
                        
                        // Get current view date range
                        const viewStart = new Date(calendarApi.view.currentStart)
                        const viewEnd = new Date(calendarApi.view.currentEnd)
                        const currentMonth = viewStart.getMonth()
                        const currentYear = viewStart.getFullYear()
                        
                        // Filter events for current month
                        const monthEvents = currentEvents.filter(evt => {
                          const eventDate = /^\d{4}-\d{2}-\d{2}$/.test(evt.start) ? parseLocalDate(evt.start) : new Date(evt.start)
                          return eventDate >= viewStart && eventDate < viewEnd
                        })
                        
                        // Generate calendar grid for the month
                        const firstDay = new Date(currentYear, currentMonth, 1)
                        const lastDay = new Date(currentYear, currentMonth + 1, 0)
                        const daysInMonth = lastDay.getDate()
                        const startingDayOfWeek = firstDay.getDay() // 0 = Sunday
                        
                        // Group events by date
                        const eventsByDate: Record<string, Array<{ title: string; type: string }>> = {}
                        monthEvents.forEach(evt => {
                          const dateKey = toLocalDateString(/^\d{4}-\d{2}-\d{2}$/.test(evt.start) ? parseLocalDate(evt.start) : new Date(evt.start))
                          if (!eventsByDate[dateKey]) {
                            eventsByDate[dateKey] = []
                          }
                          eventsByDate[dateKey].push({
                            title: evt.title,
                            type: evt.extendedProps?.type || 'event'
                          })
                        })
                        
                        // Generate calendar HTML
                        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
                        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
                        
                        let calendarHTML = '<table style="width: 100%; border-collapse: collapse; margin-top: 20px;">'
                        calendarHTML += '<thead><tr>'
                        dayNames.forEach(day => {
                          calendarHTML += `<th style="border: 1px solid #ddd; padding: 10px; background: #f0f0f0; text-align: center;">${day}</th>`
                        })
                        calendarHTML += '</tr></thead><tbody><tr>'
                        
                        // Empty cells for days before month starts
                        for (let i = 0; i < startingDayOfWeek; i++) {
                          calendarHTML += '<td style="border: 1px solid #ddd; padding: 10px; min-height: 100px; background: #f9f9f9;"></td>'
                        }
                        
                        // Days of the month
                        for (let day = 1; day <= daysInMonth; day++) {
                          const date = new Date(currentYear, currentMonth, day)
                          const dateKey = toLocalDateString(date)
                          const dayEvents = eventsByDate[dateKey] || []
                          
                          calendarHTML += `<td style="border: 1px solid #ddd; padding: 8px; min-height: 100px; vertical-align: top;">`
                          calendarHTML += `<div style="font-weight: bold; margin-bottom: 4px;">${day}</div>`
                          dayEvents.forEach(evt => {
                            const bgColor = evt.type === 'holiday' ? '#f59e0b' : '#3b82f6'
                            calendarHTML += `<div style="background: ${bgColor}; color: white; padding: 2px 4px; margin: 2px 0; font-size: 11px; border-radius: 3px;">${esc(evt.title)}</div>`
                          })
                          calendarHTML += '</td>'
                          
                          // New row every 7 days
                          if ((day + startingDayOfWeek) % 7 === 0 && day < daysInMonth) {
                            calendarHTML += '</tr><tr>'
                          }
                        }
                        
                        // Empty cells for days after month ends
                        const remainingCells = 7 - ((daysInMonth + startingDayOfWeek) % 7)
                        if (remainingCells < 7) {
                          for (let i = 0; i < remainingCells; i++) {
                            calendarHTML += '<td style="border: 1px solid #ddd; padding: 10px; background: #f9f9f9;"></td>'
                          }
                        }
                        
                        calendarHTML += '</tr></tbody></table>'
                        
                        // Generate HTML for print
                        const printHTML = `
                          <!DOCTYPE html>
                          <html>
                            <head>
                              <title>${esc(title)}</title>
                              <style>
                                @media print {
                                  @page { margin: 0.5in; }
                                  body { margin: 0; }
                                }
                                body { font-family: Arial, sans-serif; padding: 20px; }
                                h1 { margin-bottom: 10px; }
                                .info { margin-bottom: 15px; }
                              </style>
                            </head>
                            <body>
                              <h1>${esc(title)}</h1>
                              <div class="info">
                                <p><strong>Parish:</strong> ${esc(parishName || 'N/A')}</p>
                                <p><strong>Class:</strong> ${esc(classDetails?.name || cls.name)}</p>
                                <p><strong>Month:</strong> ${monthNames[currentMonth]} ${currentYear}</p>
                              </div>
                              ${calendarHTML}
                              <script>
                                window.onload = () => {
                                  setTimeout(() => window.print(), 100);
                                };
                              </script>
                            </body>
                          </html>
                        `
                        
                        printWindow.document.write(printHTML)
                        printWindow.document.close()
                        addToast({ message: 'Opening print preview...', type: 'success' })
                      } catch (error) {
                        console.error('Failed to print calendar:', error)
                        addToast({ message: 'Failed to print calendar', type: 'error' })
                      }
                    } else if (!parishName || !classDetails) {
                      addToast({ message: 'Loading class details...', type: 'info' })
                      return
                    } else {
                      // Print student list
                      try {
                        const allStudents = await studentsApi.list()
                        const classStudents = allStudents.filter(s => s.class_id === cls.id)
                        printClassStudentList({
                          parishName,
                          className: cls.name,
                          teachers: classDetails.teacher_names || [],
                          students: classStudents,
                        })
                        addToast({ message: 'Print dialog opened', type: 'success' })
                      } catch (error) {
                        console.error('Failed to print class student list:', error)
                        addToast({ message: 'Failed to print student list', type: 'error' })
                      }
                    }
                  }}
                  title="Print the class student list with parish name, class name, teachers, and all assigned students"
                  aria-label="Print student list"
                >
                  🖨️ Print
                </button>
                <button 
                  className="btn-secondary text-sm focus-ring" 
                  onClick={onClose}
                  aria-label="Close calendar"
                >
                  ✕ Close
                </button>
              </div>
            </div>
            {/* Main Content Area */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {loading ? (
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <Spinner label="Loading calendar…" />
                </div>
              ) : fcLoaded && FullCalendarComp && viewMode === 'calendar' ? (
                <div className="flex flex-col h-full space-y-4">
                  <div className="flex-1 min-h-[500px]">
                    <FullCalendarComp
                      ref={setCalendarRef}
                      plugins={fcPlugins}
                      initialView="dayGridMonth"
                      height="auto"
                      events={calendarEvents.map(evt => ({
                        ...evt,
                        className: selectedEventIds.has(evt.id || '') ? 'fc-event-selected border-4 border-blue-500' : '',
                      }))}
                      editable
                      selectable
                      eventClick={(info: any) => {
                        const event = info.event
                        const eventId = String(event.id)
                        const ctrlKey = info.jsEvent.ctrlKey || info.jsEvent.metaKey
                        
                        // Handle multi-select
                        if (ctrlKey && event.extendedProps?.type === 'event') {
                          toggleEventSelection(eventId, true)
                          info.jsEvent.preventDefault()
                          return
                        }
                        
                        // Regular click - select single or open modal
                        if (event.extendedProps?.type === 'event') {
                          if (selectedEventIds.has(eventId) && selectedEventIds.size === 1) {
                            handleEventClick(info)
                          } else {
                            toggleEventSelection(eventId, false)
                          }
                        } else {
                          handleEventClick(info)
                        }
                      }}
                      select={(info: any) => {
                        // Box selection - select all events in range
                        const start = info.start
                        const end = info.end
                        const selectedIds = new Set<string>()
                        
                        events.forEach(event => {
                          if (!event.event_date) return
                          const eventDate = new Date(event.event_date)
                          if (eventDate >= start && eventDate < end) {
                            selectedIds.add(event.id)
                          }
                        })
                        
                        if (selectedIds.size > 0) {
                          setSelectedEventIds(selectedIds)
                          addToast({ message: `Selected ${selectedIds.size} event(s)`, type: 'info' })
                        }
                        
                        // Clear the selection rectangle
                        info.view.calendar.unselect()
                      }}
                      eventDrop={async (info: any) => {
                        // Only allow dragging class events, not holidays or birthdays
                        if (info.event.extendedProps?.type !== 'event') {
                          info.revert()
                          addToast({ message: 'Only class events can be moved', type: 'info' })
                          return
                        }

                        const draggedEventId = String(info.event.id)
                        const oldStart: Date | null = info.oldEvent?.start ?? null
                        const newStart: Date | null = info.event.start ?? null
                        if (!oldStart || !newStart) {
                          info.revert()
                          return
                        }
                        const dayDelta = Math.round(
                          (new Date(newStart.getFullYear(), newStart.getMonth(), newStart.getDate()).getTime() -
                            new Date(oldStart.getFullYear(), oldStart.getMonth(), oldStart.getDate()).getTime()) / 86400000
                        )
                        // Dragging one of several selected events moves them all
                        const ids = selectedEventIds.has(draggedEventId) && selectedEventIds.size > 1
                          ? Array.from(selectedEventIds)
                          : [draggedEventId]
                        await moveEventsByDays(ids, dayDelta)
                      }}
                      eventDragStart={(info: any) => {
                        // If this event is part of selection, maintain selection
                        const eventId = String(info.event.id)
                        if (!selectedEventIds.has(eventId)) {
                          // Select only this event if not already selected
                          setSelectedEventIds(new Set([eventId]))
                        }
                      }}
                      dateClick={(arg: any) => {
                        // If paste mode active, paste events here
                        if (isSelecting && clipboardEvents.length > 0) {
                          handlePaste(arg.dateStr)
                          return
                        }

                        // If events selected and Ctrl/Cmd held, paste here
                        const ctrlKey = arg.jsEvent.ctrlKey || arg.jsEvent.metaKey
                        if (ctrlKey && clipboardEvents.length > 0) {
                          handlePaste(arg.dateStr)
                          return
                        }

                        // Clear selection on date click (unless Ctrl/Cmd held)
                        if (!ctrlKey && !arg.jsEvent.shiftKey) {
                          setSelectedEventIds(new Set())
                        }

                        // Pre-fill date when clicking on calendar
                        const when = new Date(arg.dateStr + 'T12:00')
                        const year = when.getFullYear()
                        const month = String(when.getMonth() + 1).padStart(2, '0')
                        const day = String(when.getDate()).padStart(2, '0')
                        const hours = '18' // Default to 6 PM
                        const minutes = '00'
                        setDate(`${year}-${month}-${day}T${hours}:${minutes}`)
                        // Switch to list view and show form immediately
                        setViewMode('list')
                        setShowCreateForm(true)
                        // Scroll to form after a brief delay
                        setTimeout(() => {
                          document.getElementById('event-topic-input')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                          document.getElementById('event-topic-input')?.focus()
                        }, 100)
                        addToast({ message: 'Date selected - fill in details below', type: 'info' })
                      }}
                      headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,dayGridWeek',
                      }}
                      dayCellClassNames={(arg: any) => {
                        // Combine holiday highlighting with paste mode feedback
                        const date = arg.date
                        const holidayCheck = isInHolidayWeek(date, holidays)
                        const classes: string[] = []
                        if (holidayCheck.isHoliday) {
                          classes.push('bg-yellow-50/50')
                        }
                        if (isSelecting) {
                          classes.push('bg-blue-50/30 cursor-copy')
                        }
                        return classes.join(' ')
                      }}
                      eventDisplay="block"
                      eventTimeFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        meridiem: 'short',
                      }}
                      eventClassNames={(info: any) => {
                        const type = info.event.extendedProps?.type
                        if (type === 'holiday') return 'cursor-default'
                        if (type === 'birthday') return 'cursor-default'
                        const eventId = String(info.event.id)
                        const isSelected = selectedEventIds.has(eventId)
                        return `cursor-move hover:opacity-80 ${isSelected ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`
                      }}
                      slotMinTime="06:00:00"
                      slotMaxTime="22:00:00"
                      expandRows={true}
                      stickyHeaderDates={true}
                      dayHeaderFormat={{ weekday: 'short' }}
                      slotLabelFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        meridiem: 'short',
                      }}
                      selectConstraint={{
                        start: '06:00',
                        end: '22:00',
                      }}
                    />
                    {isSelecting && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                        📌 Paste mode: Click on a date to paste {clipboardEvents.length} event(s) there
                        <button 
                          className="ml-2 text-blue-600 underline"
                          onClick={() => {
                            setIsSelecting(false)
                            setClipboardMode(null)
                            setClipboardEvents([])
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    {selectedEventIds.size > 0 && viewMode === 'calendar' && (
                      <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                        💡 Tip: Hold Ctrl/Cmd and click events to multi-select. Drag to move, or use Copy/Cut/Paste buttons above.
                      </div>
                    )}
                  </div>
                  {/* Legend */}
                  <div className="flex flex-wrap items-center justify-center gap-6 text-xs pt-3 pb-2 border-t border-gray-200 bg-gray-50 rounded-b-lg px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-500 rounded shadow-sm"></div>
                      <span className="font-medium text-gray-700">Class Events</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-amber-500 rounded shadow-sm"></div>
                      <span className="font-medium text-gray-700">Holidays</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-pink-500 rounded shadow-sm"></div>
                      <span className="font-medium text-gray-700">Birthdays</span>
                    </div>
                    <div className="text-gray-500 text-xs">
                      💡 Tip: Click dates to create events • Drag events to reschedule
                    </div>
                  </div>
                </div>
              ) : viewMode === 'list' ? (
                <div className="space-y-4 overflow-y-auto flex-1">
                  {/* Create New Event button - only show if form is hidden */}
                  {!showCreateForm && (
                    <div className="flex justify-start mb-4">
                      <button
                        type="button"
                        className="btn-primary text-sm focus-ring"
                        onClick={() => {
                          setShowCreateForm(true)
                          // Clear form
                          setTopic('')
                          setNotes('')
                          setDate('')
                          setRepeatEnabled(false)
                          // Scroll to form after a brief delay
                          setTimeout(() => {
                            document.getElementById('event-topic-input')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            document.getElementById('event-topic-input')?.focus()
                          }, 100)
                        }}
                        aria-label="Create new event"
                      >
                        ➕ Create New Event
                      </button>
                    </div>
                  )}
                  
                  {/* Event creation form - only show if showCreateForm is true */}
                  {showCreateForm && (
                  <div className="border-2 border-gray-200 rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">Create New Event</h3>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="bg-gradient-to-r from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-200 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 focus-ring border border-yellow-300"
                          onClick={() => {
                            setShowCreateForm(false)
                            setTopic('')
                            setNotes('')
                            setDate('')
                            setRepeatEnabled(false)
                          }}
                          aria-label="Cancel and return to list view"
                        >
                          Cancel
                        </button>
                        <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full border border-gray-200">
                          Quick Add
                        </span>
                      </div>
                    </div>
                    <form
                      className="space-y-3"
                      onSubmit={async (e) => {
                        e.preventDefault()
                        if (!topic.trim() || !date) {
                          addToast({ message: 'Please fill in topic and date', type: 'error' })
                          return
                        }
                        if (weeklyStartMismatch) {
                          addToast({ message: 'The start date must fall on one of the selected repeat days.', type: 'error' })
                          return
                        }
                        setSubmitting(true)
                        try {
                          const start = new Date(date)
                          const occurrenceDates = generateOccurrences(start, {
                            repeatEnabled,
                            repeatFreq,
                            repeatInterval,
                            repeatEndMode,
                            repeatUntil,
                            repeatCount,
                            repeatWeekdays,
                            monthlyMode,
                            monthlyOrdinal,
                            skipHolidays,
                            holidays,
                            rrule: rruleLoaded ? RRuleObj : null,
                          })

                          // All occurrences in one recurring series share a single series id
                          // so "Edit all events in series" can find them.
                          const seriesId = repeatEnabled ? crypto.randomUUID() : null
                          await Promise.all(
                            occurrenceDates.map((when) =>
                              eventsApi.create({
                                class_id: cls.id,
                                topic: topic.trim(),
                                notes: notes.trim() || null,
                                // Local wall-clock datetime; toISOString() would shift the day west of UTC.
                                event_date: toLocalDateTimeString(when),
                                is_recurring: repeatEnabled,
                                recurrence_rule: null,
                                recurrence_series_id: seriesId,
                              })
                            )
                          )

                          addToast({ message: `Created ${occurrenceDates.length} event${occurrenceDates.length !== 1 ? 's' : ''}`, type: 'success' })
                          await handleRefresh()
                          setTopic('')
                          setNotes('')
                          setDate('')
                          setRepeatEnabled(false)
                          setRepeatUntil('')
                          setRepeatCount('')
                          setRepeatWeekdays(new Set())
                        } catch (error: any) {
                          console.error('Failed to create events:', error)
                          addToast({
                            message: error.response?.data?.detail || 'Failed to create events',
                            type: 'error',
                          })
                        } finally {
                          setSubmitting(false)
                        }
                      }}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-700">
                            Topic <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="event-topic-input"
                            className="input-field w-full focus-ring"
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            placeholder="e.g., Lesson 5: The Sacraments"
                            required
                            aria-required="true"
                            aria-label="Event topic"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-700">
                            Date & Time <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="datetime-local"
                            step="900"
                            className="input-field w-full focus-ring"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            required
                            aria-required="true"
                            aria-label="Event date and time"
                          />
                          {date && (
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(date).toLocaleString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-semibold text-gray-700">
                            Notes <span className="text-gray-400 font-normal">(optional)</span>
                          </label>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            notes.trim().split(/\s+/).filter(w => w.length > 0).length > 1023 
                              ? 'text-red-600 bg-red-50' 
                              : notes.trim().split(/\s+/).filter(w => w.length > 0).length > 950
                              ? 'text-yellow-600 bg-yellow-50'
                              : 'text-gray-500 bg-gray-100'
                          }`}>
                            {notes.trim().split(/\s+/).filter(w => w.length > 0).length}/1023 words
                          </span>
                        </div>
                        <textarea
                          className="input-field w-full h-28 resize-y focus-ring"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Add reminders, homework assignments, special instructions, etc."
                          aria-label="Event notes"
                        />
                        {notes.trim().split(/\s+/).filter(w => w.length > 0).length > 1023 && (
                          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                            <span>⚠️</span>
                            <span>Exceeds word limit. Please reduce to 1023 words or less.</span>
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 p-3 bg-white rounded-lg border border-gray-200">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            id="repeatEnabled"
                            type="checkbox"
                            className="w-4 h-4 text-yellow-500 focus-ring rounded border-gray-300"
                            checked={repeatEnabled}
                            onChange={(e) => {
                              setRepeatEnabled(e.target.checked)
                              // Pre-select the start date's weekday so the form is valid straight away
                              if (e.target.checked && repeatWeekdays.size === 0 && date) {
                                const d = new Date(date.length === 10 ? `${date}T00:00` : date)
                                if (!isNaN(d.getTime())) setRepeatWeekdays(new Set([d.getDay()]))
                              }
                            }}
                            aria-label="Enable recurring event"
                          />
                          <span className="text-sm font-medium text-gray-700">Repeat this event</span>
                        </label>
                        {repeatEnabled && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              id="skipHolidays"
                              type="checkbox"
                              className="w-4 h-4 text-yellow-500 focus-ring rounded border-gray-300"
                              checked={skipHolidays}
                              onChange={(e) => setSkipHolidays(e.target.checked)}
                              aria-label="Skip holiday weeks"
                            />
                            <span className="text-sm text-gray-600">Skip holiday weeks</span>
                            <span className="text-xs text-gray-400" title="Automatically skip weeks containing holidays">ℹ️</span>
                          </label>
                        )}
                        {repeatEnabled && skipHolidays && (
                          <div className="text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded border border-gray-200">
                            <div className="font-medium mb-1">Holidays that will be skipped:</div>
                            <ul className="list-disc list-inside space-y-0.5 text-gray-600">
                              <li>January 1: The Solemnity of Mary, the Holy Mother of God</li>
                              <li>Easter + Easter Monday</li>
                              <li>Thanksgiving</li>
                              <li>December 25: The Solemnity of the Nativity of the Lord (Christmas)</li>
                            </ul>
                          </div>
                        )}
                      </div>
                      {repeatEnabled && (
                        <div className="p-4 bg-white rounded-lg border-2 border-gray-200 space-y-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Recurrence Settings</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-xs font-semibold mb-1.5 text-gray-700">
                                Frequency
                              </label>
                              <select
                                className="input-field w-full focus-ring text-sm"
                                value={repeatFreq}
                                onChange={(e) => {
                                  setRepeatFreq(e.target.value as any)
                                  // Auto-select the start date's weekday for weekly
                                  if (e.target.value === 'WEEKLY' && date && repeatWeekdays.size === 0) {
                                    const startDate = new Date(date)
                                    setRepeatWeekdays(new Set([startDate.getDay()]))
                                  }
                                }}
                                aria-label="Repeat frequency"
                              >
                                <option value="DAILY">Daily</option>
                                <option value="WEEKLY">Weekly</option>
                                <option value="MONTHLY">Monthly</option>
                              </select>
                            </div>
                            {repeatFreq === 'WEEKLY' && (
                              <div className="sm:col-span-2 lg:col-span-3">
                                <label className="block text-xs font-semibold mb-1.5 text-gray-700">
                                  Days of Week
                                </label>
                                <div className="flex flex-wrap gap-2">
                                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                                    <label
                                      key={idx}
                                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 cursor-pointer transition-all ${
                                        repeatWeekdays.has(idx)
                                          ? 'border-yellow-500 bg-yellow-50 text-yellow-900 font-medium'
                                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 text-yellow-500 focus-ring rounded border-gray-300"
                                        checked={repeatWeekdays.has(idx)}
                                        onChange={(e) => {
                                          const newSet = new Set(repeatWeekdays)
                                          if (e.target.checked) {
                                            newSet.add(idx)
                                          } else {
                                            newSet.delete(idx)
                                            // Don't allow unchecking all days
                                            if (newSet.size === 0) {
                                              return
                                            }
                                          }
                                          setRepeatWeekdays(newSet)
                                        }}
                                        aria-label={`Repeat on ${day}`}
                                      />
                                      <span className="text-xs">{day}</span>
                                    </label>
                                  ))}
                                </div>
                                {repeatWeekdays.size === 0 && (
                                  <p className="text-xs text-red-600 mt-1">⚠️ Select at least one day of the week</p>
                                )}
                                {weeklyStartMismatch && repeatWeekdays.size > 0 && startWeekday !== null && (
                                  <p className="text-xs text-red-600 mt-1">
                                    ⚠️ The start date is a {WEEKDAY_NAMES[startWeekday]}. Add {WEEKDAY_NAMES[startWeekday]} to the repeat days above, or change the start date so it matches one of them.
                                  </p>
                                )}
                              </div>
                            )}
                            {repeatFreq === 'MONTHLY' && (
                              <div className="sm:col-span-2 lg:col-span-3">
                                <label className="block text-xs font-semibold mb-1.5 text-gray-700">
                                  Monthly pattern
                                </label>
                                <div className="flex flex-wrap items-center gap-2">
                                  <select
                                    className="input-field focus-ring text-sm"
                                    value={monthlyMode}
                                    onChange={(e) => {
                                      const mode = e.target.value as 'DAY_OF_MONTH' | 'NTH_WEEKDAY'
                                      setMonthlyMode(mode)
                                      // Pre-select the ordinal that matches the start date.
                                      if (mode === 'NTH_WEEKDAY' && date) {
                                        setMonthlyOrdinal(Math.ceil(new Date(date).getDate() / 7))
                                      }
                                    }}
                                    aria-label="Monthly recurrence pattern"
                                  >
                                    <option value="DAY_OF_MONTH">On day {date ? new Date(date).getDate() : '—'} of the month</option>
                                    <option value="NTH_WEEKDAY">On the …</option>
                                  </select>
                                  {monthlyMode === 'NTH_WEEKDAY' && (
                                    <>
                                      <select
                                        className="input-field focus-ring text-sm"
                                        value={monthlyOrdinal}
                                        onChange={(e) => setMonthlyOrdinal(Number(e.target.value))}
                                        aria-label="Which occurrence in the month"
                                      >
                                        <option value={1}>First</option>
                                        <option value={2}>Second</option>
                                        <option value={3}>Third</option>
                                        <option value={4}>Fourth</option>
                                        <option value={-1}>Last</option>
                                      </select>
                                      <span className="text-sm font-medium text-gray-700">
                                        {startWeekday !== null ? WEEKDAY_NAMES[startWeekday] : 'weekday'} of each month
                                      </span>
                                    </>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                  {monthlyMode === 'DAY_OF_MONTH'
                                    ? 'Repeats on the same calendar day each month.'
                                    : startWeekday !== null
                                      ? `Repeats on the ${['', 'first', 'second', 'third', 'fourth'][monthlyOrdinal] || 'last'} ${WEEKDAY_NAMES[startWeekday]} of each month.`
                                      : 'Pick a start date to set the weekday.'}
                                </p>
                              </div>
                            )}
                            <div>
                              <label className="block text-xs font-semibold mb-1.5 text-gray-700">
                                Every
                              </label>
                              <input
                                type="number"
                                min={1}
                                max={52}
                                className="input-field w-full focus-ring text-sm"
                                value={repeatInterval}
                                onChange={(e) => setRepeatInterval(Math.max(1, Number(e.target.value) || 1))}
                                aria-label="Repeat interval"
                              />
                              <p className="text-xs text-gray-400 mt-1">
                                {(() => {
                                  const unit = repeatFreq === 'DAILY' ? 'day' : repeatFreq === 'WEEKLY' ? 'week' : 'month'
                                  return repeatInterval === 1 ? unit : `${unit}s`
                                })()}
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold mb-1.5 text-gray-700">
                                End After
                              </label>
                              <select
                                className="input-field w-full focus-ring text-sm"
                                value={repeatEndMode}
                                onChange={(e) => setRepeatEndMode(e.target.value as any)}
                                aria-label="Repeat end mode"
                              >
                                <option value="until">On date</option>
                                <option value="count">After count</option>
                              </select>
                            </div>
                            <div>
                              {repeatEndMode === 'until' ? (
                                <>
                                  <label className="block text-xs font-semibold mb-1.5 text-gray-700">
                                    End Date
                                  </label>
                                  <input
                                    type="date"
                                    className="input-field w-full focus-ring text-sm"
                                    value={repeatUntil}
                                    onChange={(e) => setRepeatUntil(e.target.value)}
                                    aria-label="Repeat until date"
                                  />
                                </>
                              ) : (
                                <>
                                  <label className="block text-xs font-semibold mb-1.5 text-gray-700">
                                    Number of Occurrences
                                  </label>
                                  <input
                                    type="number"
                                    min={1}
                                    max={365}
                                    className="input-field w-full focus-ring text-sm"
                                    value={repeatCount}
                                    onChange={(e) => setRepeatCount(e.target.value ? Number(e.target.value) : '')}
                                    aria-label="Number of occurrences"
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-200">
                        <div className="text-xs text-gray-500 min-w-0">
                          {repeatEnabled && recurrencePreview.length > 0 && (
                            <span className="inline-flex items-start gap-1">
                              <span>🔄</span>
                              <span>
                                Creating {recurrencePreview.length} event{recurrencePreview.length !== 1 ? 's' : ''}: {recurrencePreview.slice(0, 4).map(fmtPreview).join(', ')}
                                {recurrencePreview.length > 4 ? `, +${recurrencePreview.length - 4} more` : ''}
                              </span>
                            </span>
                          )}
                        </div>
                        <button
                          type="submit"
                          className="btn-primary focus-ring min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={submitting || !topic.trim() || !date || weeklyStartMismatch || (notes.trim().split(/\s+/).filter(w => w.length > 0).length > 1023)}
                          title={weeklyStartMismatch ? 'The start date must fall on one of the selected repeat days.' : repeatEnabled ? "Create a recurring event that will repeat based on your selected schedule. Enable 'skip holiday weeks' to avoid creating events during holiday weeks." : "Create a single event for the selected date. Enable recurrence to create multiple events automatically."}
                          aria-label="Create event"
                        >
                          {submitting ? (
                            <span className="flex items-center gap-2">
                              <span className="animate-spin-smooth">⏳</span>
                              <span>Creating...</span>
                            </span>
                          ) : (
                            '✨ Create Event'
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                  )}

                  {/* Events list */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold text-gray-900">All Events</h3>
                      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        {sorted.length} {sorted.length === 1 ? 'event' : 'events'}
                      </span>
                    </div>
                    {sorted.length === 0 ? (
                      <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                        <div className="text-4xl mb-3">📅</div>
                        <p className="text-sm font-medium text-gray-700 mb-1">No events yet</p>
                        <p className="text-xs text-gray-500">Create your first event using the form above</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-xs text-gray-500">Tip: Ctrl/⌘-click rows (or tick the boxes) to select several • drag an event onto another event's row to move it (and any selected events) to that day • Copy / Cut / Paste above.</p>
                      <div className="divide-y divide-gray-100 border-2 border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                        {sorted.map(ev => {
                          const eventDate = ev.starts_at || ev.event_date
                          const isPast = eventDate ? new Date(eventDate) < new Date() : false
                          const isDragging = draggedEventId === ev.id
                          return (
                            <div
                              key={ev.id}
                              draggable
                              onDragStart={(e) => {
                                setDraggedEventId(ev.id)
                                e.dataTransfer.effectAllowed = 'move'
                                e.dataTransfer.setData('text/plain', ev.id)
                              }}
                              onDragEnd={() => {
                                setDraggedEventId(null)
                                setDropOverId(null)
                              }}
                              onDragOver={(e) => {
                                if (draggedEventId && draggedEventId !== ev.id) {
                                  e.preventDefault()
                                  if (dropOverId !== ev.id) setDropOverId(ev.id)
                                }
                              }}
                              onDragLeave={() => { if (dropOverId === ev.id) setDropOverId(null) }}
                              onDrop={async (e) => {
                                e.preventDefault()
                                const dragged = events.find(x => x.id === draggedEventId)
                                setDraggedEventId(null)
                                setDropOverId(null)
                                if (!dragged?.event_date || !ev.event_date || dragged.id === ev.id) return
                                const delta = Math.round((parseLocalDate(ev.event_date).getTime() - parseLocalDate(dragged.event_date).getTime()) / 86400000)
                                const ids = selectedEventIds.has(dragged.id) && selectedEventIds.size > 1 ? Array.from(selectedEventIds) : [dragged.id]
                                await moveEventsByDays(ids, delta)
                              }}
                              className={`p-4 hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200 cursor-move group ${
                                isDragging ? 'opacity-50' : ''
                              } ${selectedEventIds.has(ev.id) ? 'bg-blue-50 ring-2 ring-inset ring-blue-400' : ''} ${
                                dropOverId === ev.id ? 'ring-2 ring-inset ring-yellow-500 bg-yellow-50' : ''
                              }`}
                              onClick={(e) => {
                                if (draggedEventId) return
                                if (e.ctrlKey || e.metaKey) {
                                  toggleEventSelection(ev.id, true)
                                  return
                                }
                                setSelectedEvent(ev)
                                setShowEventModal(true)
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  setSelectedEvent(ev)
                                  setShowEventModal(true)
                                }
                              }}
                              aria-label={`Event: ${ev.topic}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <input
                                      type="checkbox"
                                      className="w-4 h-4"
                                      checked={selectedEventIds.has(ev.id)}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={() => toggleEventSelection(ev.id, true)}
                                      aria-label={`Select event: ${ev.topic}`}
                                    />
                                    <span className="text-gray-400 cursor-grab active:cursor-grabbing" title="Drag onto another event to move it to that day">
                                      ⋮⋮
                                    </span>
                                    <div className={`w-2 h-2 rounded-full ${isPast ? 'bg-gray-400' : 'bg-blue-500'} flex-shrink-0`}></div>
                                    <div className={`font-semibold text-sm ${isPast ? 'text-gray-500' : 'text-gray-900'}`}>
                                      {ev.topic}
                                    </div>
                                    {ev.is_recurring && (
                                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                        🔄 Recurring
                                      </span>
                                    )}
                                  </div>
                                  {ev.notes && (
                                    <div className="text-xs text-gray-600 mt-1.5 line-clamp-2 pl-4">
                                      {ev.notes}
                                    </div>
                                  )}
                                  <div className="pl-4"><AttachmentLinks ids={ev.attachment_ids} /></div>
                                  <div className="text-xs text-gray-500 mt-2 pl-4 flex items-center gap-2">
                                    <span>📆</span>
                                    <span>
                                      {formatEventWhen(ev.event_date)}
                                    </span>
                                    {isPast && <span className="text-gray-400">(Past)</span>}
                                  </div>
                                </div>
                                <button
                                  className="btn-secondary text-xs focus-ring ml-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setSelectedEvent(ev)
                                    setShowEventModal(true)
                                  }}
                                  aria-label={`Edit event: ${ev.topic}`}
                                >
                                  ✏️ Edit
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">Create Event</h3>
                    <button
                      type="button"
                      className="btn-secondary text-sm focus-ring"
                      onClick={() => setViewMode('calendar')}
                      aria-label="Cancel and return to calendar view"
                    >
                      Cancel
                    </button>
                  </div>
                  <form
                    className="flex flex-col sm:flex-row gap-2 items-start sm:items-end"
                    onSubmit={async (e) => {
                      e.preventDefault()
                      if (!topic.trim() || !date) return
                      setSubmitting(true)
                      try {
                        const start = new Date(date)
                        const occurrenceDates = generateOccurrences(start, {
                          repeatEnabled,
                          repeatFreq,
                          repeatInterval,
                          repeatEndMode,
                          repeatUntil,
                          repeatCount,
                          repeatWeekdays,
                          monthlyMode,
                          monthlyOrdinal,
                          skipHolidays,
                          holidays,
                          rrule: rruleLoaded ? RRuleObj : null,
                        })
                        // Clear form first
                        const topicValue = topic.trim()
                        const notesValue = notes.trim() || null
                        const repeatEnabledValue = repeatEnabled
                        setTopic('')
                        setNotes('')
                        setDate('')
                        setRepeatEnabled(false)
                        setRepeatUntil('')
                        setRepeatCount('')
                        setRepeatWeekdays(new Set())
                        
                        try {
                          // All occurrences in one series share a single series id.
                          const seriesId = repeatEnabledValue ? crypto.randomUUID() : null
                          const createPromises = occurrenceDates.map((when) =>
                            eventsApi.create({
                              class_id: cls.id,
                              topic: topicValue,
                              notes: notesValue,
                              // Local wall-clock datetime; toISOString() would shift the day west of UTC.
                              event_date: toLocalDateTimeString(when),
                              is_recurring: repeatEnabledValue,
                              recurrence_rule: null, // TODO: generate RRULE string if needed
                              recurrence_series_id: seriesId,
                            })
                          )
                          await Promise.all(createPromises)

                          // Refresh events list after creation - wait a bit for backend to commit
                          await new Promise(resolve => setTimeout(resolve, 500))
                          const refreshed = await eventsApi.list(cls.id)

                          // Always update events state, even if empty
                          setEvents(Array.isArray(refreshed) ? refreshed : [])

                          // Switch back to calendar view after successful creation
                          setViewMode('calendar')

                          // The useEffect hook will automatically refresh the calendar when events change
                          addToast({ message: `Successfully created ${occurrenceDates.length} event(s)`, type: 'success' })
                        } catch (error: any) {
                          console.error('Failed to create events:', error)
                          console.error('Error response:', error.response?.data)
                          addToast({ 
                            message: error.response?.data?.detail || 'Failed to create events. Please try again.', 
                            type: 'error' 
                          })
                        }
                      } finally {
                        setSubmitting(false)
                      }
                    }}
                  >
                    <div className="flex-1">
                      <label className="block text-xs font-medium mb-1">Topic</label>
                      <input className="input-field w-full focus-ring" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Class topic" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Date</label>
                      <input type="datetime-local" step="900" className="input-field focus-ring" value={date} onChange={(e) => setDate(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-2 pt-5">
                      <input id="repeatEnabled" type="checkbox" className="focus-ring" checked={repeatEnabled} onChange={(e) => setRepeatEnabled(e.target.checked)} />
                      <label htmlFor="repeatEnabled" className="text-sm">Repeat</label>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Frequency</label>
                        <select className="input-field focus-ring" value={repeatFreq} onChange={(e) => setRepeatFreq(e.target.value as any)} disabled={!repeatEnabled}>
                          <option value="DAILY">Daily</option>
                          <option value="WEEKLY">Weekly</option>
                          <option value="MONTHLY">Monthly (same day)</option>
                        </select>
                      </div>
                      {repeatFreq === 'MONTHLY' && (
                        <div>
                          <label className="block text-xs font-medium mb-1">Monthly mode</label>
                          <select className="input-field focus-ring" value={monthlyMode} onChange={(e) => setMonthlyMode(e.target.value as any)} disabled={!repeatEnabled}>
                            <option value="DAY_OF_MONTH">Same calendar day</option>
                            <option value="NTH_WEEKDAY">Nth weekday (e.g., 2nd Tue)</option>
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium mb-1">Interval</label>
                        <input type="number" min={1} className="input-field w-20 focus-ring" value={repeatInterval} onChange={(e) => setRepeatInterval(Number(e.target.value) || 1)} disabled={!repeatEnabled} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">End</label>
                        <select className="input-field focus-ring" value={repeatEndMode} onChange={(e) => setRepeatEndMode(e.target.value as any)} disabled={!repeatEnabled}>
                          <option value="until">On date</option>
                          <option value="count">After count</option>
                        </select>
                      </div>
                      {repeatEndMode === 'until' ? (
                        <div>
                          <label className="block text-xs font-medium mb-1">Until</label>
                          <input type="date" className="input-field focus-ring" value={repeatUntil} onChange={(e) => setRepeatUntil(e.target.value)} disabled={!repeatEnabled} />
                        </div>
                      ) : (
                        <div>
                          <label className="block text-xs font-medium mb-1">Count</label>
                          <input type="number" min={1} className="input-field w-24 focus-ring" value={repeatCount} onChange={(e) => setRepeatCount(e.target.value ? Number(e.target.value) : '')} disabled={!repeatEnabled} />
                        </div>
                      )}
                    </div>
                    <button 
                      type="submit"
                      className="btn-primary focus-ring" 
                      disabled={submitting || !topic || !date}
                      title="Add this event to the calendar. If recurrence is enabled, multiple events will be created based on your schedule."
                    >Add</button>
                  </form>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <input id="skipHolidays" type="checkbox" className="focus-ring" checked={skipHolidays} onChange={(e) => setSkipHolidays(e.target.checked)} />
                    <label htmlFor="skipHolidays">Skip US holiday weeks (server enforced)</label>
                  </div>
                  {sorted.length === 0 ? (
                    <div className="text-sm text-gray-500">No events yet.</div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {sorted.map(ev => (
                        <div key={ev.id} className="py-2 flex items-center justify-between text-sm">
                          <div>
                            <div className="font-medium">{ev.topic}</div>
                            <div className="text-xs text-gray-600">{ev.starts_at ? new Date(ev.starts_at).toLocaleString() : ev.event_date ? new Date(ev.event_date).toLocaleString() : 'No date'}</div>
                          </div>
                          <button
                            className="btn-secondary text-xs focus-ring"
                            onClick={() => {
                              setSelectedEvent(ev)
                              setShowEventModal(true)
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Event Edit Modal */}
            {showEventModal && selectedEvent && (
              <EventEditModal
                open={showEventModal}
                onClose={() => {
                  setShowEventModal(false)
                  setSelectedEvent(null)
                }}
                event={selectedEvent}
                onSaved={async () => {
                  await handleRefresh()
                  setShowEventModal(false)
                  setSelectedEvent(null)
                }}
                onDeleted={async () => {
                  await handleRefresh()
                  setShowEventModal(false)
                  setSelectedEvent(null)
                }}
              />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}


